import { GizmoHelper, GizmoViewport } from '@react-three/drei';
import { type ThreeEvent, useThree } from '@react-three/fiber';
import { debounce } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { debounceTime, filter, fromEvent, map, tap } from 'rxjs';
import type { Vector2Tuple, Vector3Tuple } from 'three';
import {
  type Material,
  Matrix4,
  type Mesh,
  Plane,
  PlaneHelper,
  Vector2,
  Vector3,
} from 'three';
import type { MapControls, OrbitControls } from 'three-stdlib';

import { TOOL_CONST } from '@/constant';
import { useShortcut, useSubscriptions, withTarget } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import {
  useEditingLabelBoxStore,
  usePcdRenderingStore,
  useTaskFrameStore,
  useToolMainCameraStore,
  useToolMenuStore,
  useToolPermissionStore,
} from '@/store';
import {
  type NEW__LabelingBox,
  assignThreeCamera,
  computeMouse,
  getOverlapRatio,
  rotateVector,
  withCanvasXY,
} from '@/util';
import appWorker from '@/worker';

import { useToolPlayControllerContext } from '../Tool/ToolPlayController';
import { DefaultViewEditingLabelBoxContainer } from './EditingLabelBox';
import ToolGrid from './ToolGrid';
import ToolPcdCamera from './ToolPcdCamera';
import type { ToolPcdCanvasProps } from './ToolPcdCanvas';
import { ToolPcdCanvasContextProvider } from './ToolPcdCanvas';
import ToolPcdCanvasMainViewShortcut from './ToolPcdCanvasMainViewShortcut';
import ToolPcdCanvasMouseHandler from './ToolPcdCanvasMouseHandler';
import { useAiAssistLoader } from './hook';

const InnerToolPcdCanvasDefaultViewMode = (props: ToolPcdCanvasProps) => {
  const { camera, controls, gl, raycaster } = useThree();
  const {
    labelingBoxList: boxList,
    selectedLabelingBoxList: selectedBoxList,
    addBox,
    clearSelectBoxList,
    selectBoxList,
  } = useLabelingBoxContext();
  const { namespace, register, unregister } = useShortcut();
  const { deselect, select, findByName, isSelectedByToolName } =
    useToolMenuStore();
  const currentFrame = useTaskFrameStore(state => state.currentFrame);
  const permission = useToolPermissionStore(state => state.permission);
  const currentFrameSeq = currentFrame?.frameSeq ?? 1;
  const [dragboxCoords, setDragboxCoords] = useState({
    cornerPointList: [] as Vector3[],
  });
  const { getMode } = useToolMainCameraStore();
  const mainCameraViewMode = getMode();
  const { $toolPlayController, isPlaybarOpen } = useToolPlayControllerContext();
  const { labelingBoxList } = useLabelingBoxContext();
  const controlsRef = useRef<MapControls | OrbitControls>(null);
  const labelingBoxListRef = useRef<NEW__LabelingBox[]>([]);
  const { getCurrentPcdData } = usePcdRenderingStore();
  const { hide: hideAiAssistLoader, show: showAiAssistLoader } =
    useAiAssistLoader();

  const transformDimensionTool = findByName('transformDimension')!;
  const transformPositionTool = findByName('transformPosition')!;
  const transformRotationTool = findByName('transformRotation')!;

  const onCanvasClick = (event: ThreeEvent<MouseEvent>) => {
    const box = event.intersections.find(v => v.object.type === 'Mesh')
      ?.object as Nullish<Mesh>;

    const boxMaterial = box?.material as Nullish<Material>;

    if (!box || !boxMaterial?.visible) return;

    const { id } = box.userData;

    if (!id?.length) return;

    select(findByName('select')!.id);

    let idList = [] as NEW__LabelingBox['id'][];

    if (event.ctrlKey || event.metaKey) {
      idList = selectedBoxList.map(b => b.id);
    }

    const idIndex = idList.findIndex(_id => _id === id);

    if (idIndex >= 0) idList.splice(idIndex, 1);
    else idList.push(id);

    selectBoxList(idList);
  };

  const onCanvasPointerMissed = (event: ThreeEvent<MouseEvent>) => {
    if (event.altKey || event.ctrlKey || event.shiftKey || event.metaKey)
      return;

    controlsRef.current?.enabled &&
      selectedBoxList.length &&
      clearSelectBoxList();
    // setVisibleTransformControls(false);
  };

  const onCreateDragbox = (coords: {
    cornerPointList: Vector3[];
    canvasPoint: {
      start: { x: number; y: number };
      last: { x: number; y: number };
    };
    dragBoxRect: DOMRect;
  }) => {
    setDragboxCoords(coords);

    if (isSelectedByToolName('aiAssist')) {
      const currentPcdData = getCurrentPcdData();

      if (!currentPcdData) return;

      const dragBox = [
        [coords.canvasPoint.start.x, coords.canvasPoint.start.y],
        [coords.canvasPoint.last.x, coords.canvasPoint.last.y],
      ] as Vector2Tuple[];

      const ground = new PlaneHelper(
        new Plane(new Vector3(0, 0, -1), 0),
        100,
        0xcccccc,
      );
      const matrix = new Matrix4();
      const canvasToWorld = (p: Vector2Tuple) => {
        raycaster.setFromCamera(new Vector2(...p), camera);

        const intersectP = new Vector3();

        raycaster.ray.intersectPlane(ground.plane, intersectP);

        return intersectP;
      };
      const worldPos = dragBox.map(p => canvasToWorld(p));
      const headAngle = Math.atan2(
        worldPos[1].y - worldPos[0].y,
        worldPos[1].x - worldPos[0].x,
      );

      matrix.copy(camera.projectionMatrix);
      matrix.multiply(camera.matrixWorldInverse);

      showAiAssistLoader({
        x: coords.dragBoxRect.x,
        y: coords.dragBoxRect.y,
        height: coords.dragBoxRect.height,
        width: coords.dragBoxRect.width,
      });

      appWorker
        .request('pcd', 'getAIMiniBox', {
          dragBox,
          frameSeq: currentFrameSeq,
          headAngle,
          heightRange: (currentPcdData.zRange?.toSorted() as Vector2Tuple) ?? [
            0, 50,
          ],
          matrix: matrix.toArray(),
          pointListBuffer: currentPcdData.pointList.buffer as ArrayBuffer,
        })
        .then(res => {
          // TODO: 여기 값 수정 필요
          const box = { ...res, label: '1' };

          addBox(box);
        })
        .finally(() => {
          hideAiAssistLoader();
        });

      deselect(findByName('aiAssist')!.id);
      select(findByName('select')!.id);
    }

    debounce(
      setDragboxCoords,
      10,
    )({
      cornerPointList: [],
    });
  };

  useEffect(() => {
    labelingBoxListRef.current = labelingBoxList;
  }, [labelingBoxList]);

  useSubscriptions(
    () => [
      fromEvent<MouseEvent>(window, 'mousemove')
        .pipe(
          filter(e => e.buttons === 0),
          withTarget,
          filter(e => e.target instanceof HTMLCanvasElement),
          withCanvasXY,
          debounceTime(10),
          assignThreeCamera(camera),
          map(event =>
            computeMouse({
              camera,
              event,
              gl,
              targetIntersections: labelingBoxListRef.current.map(
                v => v.boxMesh,
              ),
            }),
          ),
          tap(event => {
            const target = event.target as HTMLCanvasElement;
            const box = event.intersections.at(0)?.object as Nullish<Mesh>;
            const boxMaterial = box?.material as Nullish<Material>;

            if (target.getAttribute('data-cursor-priority') === 'editing') {
              return;
            }

            target.style.cursor =
              !box || !boxMaterial?.visible ? 'default' : 'pointer';
          }),
        )
        .subscribe(),
    ],
    [camera, gl],
  );

  useEffect(() => {
    const { cornerPointList } = dragboxCoords;

    if (cornerPointList.length !== 4) return;

    const isBoxOverlapping = (
      box: NEW__LabelingBox,
      polygonPointList: Vector3[],
    ) => {
      const position = box.position;
      const dimension = box.dimension;
      const quaternion = box.quaternion;

      const halfW = dimension[0] / 2;
      const halfH = dimension[1] / 2;

      const localCornerList: Vector3Tuple[] = [
        [-halfW, -halfH, 0],
        [halfW, -halfH, 0],
        [halfW, halfH, 0],
        [-halfW, halfH, 0],
      ];

      const boxCornerList = localCornerList.map(corner => {
        const rotated = rotateVector(corner, quaternion);
        return {
          x: position[0] + rotated[0],
          y: position[1] + rotated[1],
        };
      });

      const dragPolygon = polygonPointList.map(p => ({ x: p.x, y: p.y }));

      return getOverlapRatio(boxCornerList, dragPolygon) >= 0.1;
    };

    // const idList = selectedBoxList.map(b => b.id.value!);
    const idList = selectedBoxList.map(b => b.id);

    boxList.forEach(box => {
      if (isBoxOverlapping(box, cornerPointList)) {
        // idList.push(box.id.value!);
        idList.push(box.id);
      }
    });

    selectBoxList([...new Set(idList)]);
  }, [dragboxCoords, selectedBoxList]);

  // useEffect(() => {
  //   if (transformDimensionTool.isSelected) {
  //     setTransformMode('scale');
  //     return;
  //   }

  //   if (transformRotationTool.isSelected) {
  //     setTransformMode('rotate');
  //     return;
  //   }

  //   if (transformPositionTool.isSelected) {
  //     setTransformMode('translate');
  //     return;
  //   }
  // }, [toolMap]);

  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);

  useEffect(() => {
    register(transformDimensionTool.command!, () =>
      select(transformDimensionTool.id),
    );
    register(transformPositionTool.command!, () =>
      select(transformPositionTool.id),
    );
    register(transformRotationTool.command!, () =>
      select(transformRotationTool.id),
    );

    return () => {
      unregister(transformDimensionTool.command!);
      unregister(transformPositionTool.command!);
      unregister(transformRotationTool.command!);
    };
  }, [namespace]);

  return (
    <>
      <ToolGrid />

      <ToolPcdCamera
        enabledControlsUiEvent
        enabledControlsPan
        enabledControlsRotate
        initCameraOptions={props.initCameraOptions}
        mode={
          mainCameraViewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP
            ? 'orthographic'
            : 'perspective'
        }
      />

      <ToolPcdCanvasMainViewShortcut />

      <DefaultViewEditingLabelBoxContainer
        disabled={!permission.labeling.updatable}
      />

      <ToolPcdCanvasMouseHandler
        vector={
          mainCameraViewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.TOP ||
          mainCameraViewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT
            ? [0, 0, 1]
            : mainCameraViewMode === TOOL_CONST.TOOL_CAMERA_VIEW_MODE.SIDE
              ? [1, 1, 0]
              : [1, 1, 0]
        }
        onClick={e => {
          if (e.intersections.length) {
            onCanvasClick(e);
          } else {
            // NOTE: top view 에서 box 이동이 끝나고 controls.enabled 를 true 가 된 이후에야 onCanvasPointerMissed 가 호출되어야 함
            if (
              controlsRef.current?.enabled &&
              !useEditingLabelBoxStore.getState().getIsEditing()
            ) {
              onCanvasPointerMissed(e);
            }
          }
        }}
        onPointerDown={e => {
          if (
            e.intersections.length &&
            selectedBoxList.length <= 1 &&
            e.buttons === 2
          ) {
            onCanvasClick(e);
          }
        }}
        onPointerUp={e => {
          if (
            !e.intersections.length &&
            !controlsRef.current?.enabled &&
            selectedBoxList.length
          ) {
            onCanvasPointerMissed(e);
          }
        }}
        onDoubleClick={e => {
          if (e.intersections.length) {
            onCanvasClick(e);
          } else {
            onCanvasPointerMissed(e);
          }
        }}
        onCreateDragbox={onCreateDragbox}
      />

      <GizmoHelper
        alignment="bottom-right"
        margin={[
          54,
          isPlaybarOpen
            ? 98 + ($toolPlayController.current?.offsetHeight ?? 0)
            : 98,
        ]}
      >
        <GizmoViewport
          axisColors={['#c24', '#2c4', '#24c']}
          labelColor="#fff"
        />
      </GizmoHelper>
    </>
  );
};

const ToolPcdCanvasDefaultViewMode = (props: ToolPcdCanvasProps) => {
  return (
    <ToolPcdCanvasContextProvider
      {...props}
      viewMode={TOOL_CONST.TOOL_CAMERA_VIEW_MODE.DEFAULT}
    >
      <InnerToolPcdCanvasDefaultViewMode {...props} />
    </ToolPcdCanvasContextProvider>
  );
};

export default ToolPcdCanvasDefaultViewMode;
